import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import FormRow from '../../Common/Components/FormRow'
import TextInput from '../../Common/Components/TextInput'
import Fieldset from '../../Common/Components/Fieldset'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { MonetaryCallAssignments as MonetaryCallAssignmentsLanguage } from '../../../../i18n'
import { MonetaryCallAssignmentsValues } from '../Types'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

/**
 * Component properties type.
 */
type Props = {
    monetaryCallAssignmentsValues: MonetaryCallAssignmentsValues
    setMonetaryCallAssignmentsValues: React.Dispatch<React.SetStateAction<MonetaryCallAssignmentsValues>>
    supportsFinancialStatsInbound: boolean
    supportsFinancialStatsOutbound: boolean
}

/**
 * Monetary call assignments section of Financial tab.
 *
 * @param monetaryCallAssignmentsValues Object containing all values for Monetary Call Assignments.
 * @param setMonetaryCallAssignmentsValues Setter for respective property.
 * @param supportsFinancialStatsInbound Report definition property.
 * @param supportsFinancialStatsOutbound Report definition property.
 */
const MonetaryCallAssignments: React.FC<Props> = ({
    monetaryCallAssignmentsValues,
    setMonetaryCallAssignmentsValues,
    supportsFinancialStatsInbound,
    supportsFinancialStatsOutbound
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('MonetaryCallAssignments')

    const handleInboundCallsAvgOrderValChange = (newValue: string): void => {
        setMonetaryCallAssignmentsValues((prevValue) => ({
            ...prevValue,
            inboundCallsAvgOrderVal: newValue
        }))
    }

    const handleInboundStrikeRateChange = (newValue: string): void => {
        setMonetaryCallAssignmentsValues((prevValue) => ({
            ...prevValue,
            inboundStrikeRate: Number.parseFloat(newValue) || undefined
        }))
    }

    const handleOutboundCallsAvgOrderValChange = (newValue: string): void => {
        setMonetaryCallAssignmentsValues((prevValue) => ({
            ...prevValue,
            outboundCallsAvgOrderVal: newValue
        }))
    }

    const handleOutboundStrikeRateChange = (newValue: string): void => {
        setMonetaryCallAssignmentsValues((prevValue) => ({
            ...prevValue,
            outboundStrikeRate: Number.parseFloat(newValue) || undefined
        }))
    }

    return (
        <Fieldset>
            <Heading level={HeadingLevel.ONE}>
                {language.get(MonetaryCallAssignmentsLanguage.MonetaryCallAssignments_SectionHeader)}
            </Heading>
            <P>{language.get(MonetaryCallAssignmentsLanguage.MonetaryCallAssignments_SectionPara)}</P>

            {supportsFinancialStatsInbound && (
                <>
                    <Heading level={HeadingLevel.TWO}>
                        {language.get(MonetaryCallAssignmentsLanguage.InboundcallsHeader)}
                    </Heading>
                    <FormRow isInline className={'inbound-calls-avg-order-value'}>
                        <TextInput
                            value={monetaryCallAssignmentsValues.inboundCallsAvgOrderVal}
                            onChange={handleInboundCallsAvgOrderValChange}
                            labelLeft={language.get(MonetaryCallAssignmentsLanguage.InboundcallsAvgordervalueLabel)}
                        />
                        <TextInput
                            value={monetaryCallAssignmentsValues.inboundStrikeRate}
                            onChange={handleInboundStrikeRateChange}
                            labelLeft={language.get(MonetaryCallAssignmentsLanguage.InboundcallsStrikerateLabel)}
                        />
                    </FormRow>
                </>
            )}

            {supportsFinancialStatsOutbound && (
                <>
                    <Heading level={HeadingLevel.TWO}>
                        {language.get(MonetaryCallAssignmentsLanguage.OutboundcallsHeader)}
                    </Heading>
                    <FormRow isInline className={'outbound-calls-avg-order-value'}>
                        <TextInput
                            value={monetaryCallAssignmentsValues.outboundCallsAvgOrderVal}
                            onChange={handleOutboundCallsAvgOrderValChange}
                            labelLeft={language.get(MonetaryCallAssignmentsLanguage.OutboundcallsAvgordervalueLabel)}
                        />
                        <TextInput
                            value={monetaryCallAssignmentsValues.outboundStrikeRate}
                            onChange={handleOutboundStrikeRateChange}
                            labelLeft={language.get(MonetaryCallAssignmentsLanguage.OutboundcallsStrikerateLabel)}
                        />
                    </FormRow>
                </>
            )}
        </Fieldset>
    )
}

export default React.memo(MonetaryCallAssignments)
